import type { Clipboard } from "@/types/workspace/Clipboard";
import Papa from "papaparse";

/**
 * Parses clipboard data as CSV/Excel data with support for multiple separators
 * @param text - The clipboard text to parse
 * @returns Promise<ClipboardData> - Structured data with rows, columns, and metadata
 */
export async function parseClipboard(text: string): Promise<Clipboard> {
  if (!text || text.trim().length === 0) {
    throw new Error("Clipboard is empty");
  }

  const cleanText = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const separators = [",", "\t", ";"];
  let bestResult: Clipboard | null = null;
  let bestConsistency = 0;

  for (const separator of separators) {
    try {
      const result = await new Promise<Clipboard>((resolve, reject) => {
        Papa.parse(cleanText, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
          delimiter: separator,
          complete: (results: Papa.ParseResult<any>) => {
            const data = results.data as any[];
            const columns = results.meta.fields || [];

            if (columns.length === 0) {
              reject(new Error("No columns found in data"));
              return;
            }

            const validRows = data.filter((row) => {
              return Object.values(row).some(
                (value) => value !== null && value !== undefined && value !== ""
              );
            });

            if (validRows.length === 0) {
              reject(new Error("No valid data rows found"));
              return;
            }

            resolve({
              rows: validRows,
              columns,
              metadata: {
                rowCount: validRows.length,
                columnCount: columns.length,
                type: "csv",
              },
            });
          },
          error: (error: Error) => {
            reject(new Error(`Parsing error: ${error.message}`));
          },
        });
      });

      const headerColumnCount = result.columns.length;
      const consistentRows = result.rows.filter(
        (row: any) => Object.keys(row).length === headerColumnCount
      ).length;
      const consistency = consistentRows / result.rows.length;

      if (
        consistency > bestConsistency &&
        result.rows.length >= 2 &&
        headerColumnCount >= 1
      ) {
        bestResult = result;
        bestConsistency = consistency;
      }
    } catch (error) {
      continue;
    }
  }

  if (!bestResult) {
    throw new Error(
      "Could not parse clipboard data. Please ensure the data is in CSV format with comma, tab, or semicolon separators."
    );
  }

  return bestResult;
}
